
import { PiniaMapAction, PiniaMapState } from 'bits/pinia'
import { Component, Getter, Prop, State, Vue, Watch, namespace } from 'bits/vue'
import { SurfaceFilter } from 'enums'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import type { Cid, Id, Post, PostAttributes } from 'types'
import SurfaceHeader from 'vuecomponents/SurfaceHeader.vue'
import SurfacePost from 'vuecomponents/surface_post.vue'
import DoubleClickToPostMixin from 'vuemixins/double_click_to_post_mixin'
import DropToPostMixin from 'vuemixins/drop_to_post_mixin'
import GridMixin from 'vuemixins/grid_mixin'

const browser = namespace('browser')

@Component({
  components: {
    SurfaceHeader,
    SurfacePost,
  },
  mixins: [GridMixin, DoubleClickToPostMixin, DropToPostMixin],
})
export default class SurfaceContainerGrid extends Vue {
  $refs!: {
    wallContainer: HTMLDivElement
    surfaceHeader: SurfaceHeader
    wishList: Vue & {
      // Transition group
      $el: HTMLDivElement
    }
  }

  @Prop({ default: () => {} }) reactionIdsByPostId!: Record<Id, Id[]>
  @Prop({ default: () => {} }) commentIdsByPostId!: Record<Id, Id[]>

  @State xHeader
  @State inlinePostEdit
  @State isMobile!: boolean
  @State isTouchable!: boolean
  @Getter isPostable
  @Getter dir!: 'rtl' | 'ltr'
  @Getter isReadOnly!: boolean
  @Getter backgroundLuminance!: 'light' | 'dark'
  @browser.Getter windowWidth
  @namespace('postFilter').Getter filter!: SurfaceFilter
  @PiniaMapState(useSurfacePostsStore) allSortedPosts!: Post[]
  @PiniaMapAction(useSurfacePostsStore) shouldDisplayPost!: (postCid: Cid) => boolean
  @namespace('post').Action sortedPost
  @namespace('post').Action createPost
  @namespace('draft').Action startNewDraft!: (payload: {
    attributes: PostAttributes
    shouldStartEditing: boolean
  }) => void

  @namespace('composerModal').Action('dock') dockComposerModal!: () => void
  initializeGrid!: (
    node: Node,
    savePostHandler: (data: { postCid; sectionId: never; previousCid; nextCid }) => void,
    options: Record<any, any>,
  ) => void // from GridMixin

  debouncedUpdateGrid!: () => void // from GridMixin
  enableDoubleClickToPost!: (wallContainer: Element, wishList: Element) => void // from DoubleClickToPostMixin
  disableDoubleClickToPost!: (wallContainer: Element) => void // from DoubleClickToPostMixin
  enableDropToPost!: (wallContainer: Element) => void // from DropToPostMixin
  disableDropToPost!: (wallContainer: Element) => void // from DropToPostMixin

  mounted(): void {
    this.initializeGrid(
      this.$refs.wallContainer as Node,
      (data: { postCid; sectionId: never; previousCid; nextCid }) => {
        this.sortedPost(data)
      },
      { rtl: this.dir === 'rtl' },
    )

    if (this.isPostable) {
      this.enableNewPostHandlers()
    }
  }

  isAnimationPermitted = false

  @Watch('filter')
  startAnimationWhenFilterChanged(): void {
    this.isAnimationPermitted = true
  }

  endAnimationForFilterChanged(): void {
    setTimeout(() => {
      this.isAnimationPermitted = false
    }, 100) // the animation still need a little bit of time ~50-100ms for the transition to be totally over
  }

  beforeDestroy(): void {
    this.disableNewPostHandlers()
  }

  enableNewPostHandlers(): void {
    if (!this.isMobile) {
      this.enableDoubleClickToPost(this.$el, this.$refs.wishList.$el)
    }
    this.enableDropToPost(this.$el)
  }

  disableNewPostHandlers(): void {
    this.disableDoubleClickToPost(this.$el)
    this.disableDropToPost(this.$el)
  }

  @Watch('isPostable')
  toggleNewPostHandlers(newValue): void {
    newValue ? this.enableNewPostHandlers() : this.disableNewPostHandlers()
  }

  @Watch('windowWidth')
  updateGridOnWindowWidthChange(): void {
    this.debouncedUpdateGrid()
  }

  get postWidth(): string {
    // returns the desired post width
    // this used to be done in scss but it caused some bugs due to roundings + we are moving away from scss
    // => do it here

    if (this.windowWidth < 481) {
      // @include respondToPhone {
      //   $margin: 8px;
      //   .wish-sizer,
      //   .wish {
      //     // we had a 0.5px so that things are rounded down, always
      //     width: calc(50% - #{3 * $margin/2} - 0.5px);
      //   }
      // }

      const wishListMargin = 8
      return `calc(50% - ${(3 * wishListMargin) / 2}px - 0.5px)`
    } else if (this.windowWidth < 1024) {
      // @include respondToPortraitTablet {
      //   $margin: 15px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(33.3% - #{4 * $margin/3} - 0.5px);
      //   }
      // }

      const wishListMargin = 15
      return `calc(33.3% - ${(4 * wishListMargin) / 3}px - 0.5px)`
    } else if (this.windowWidth === 1024) {
      // @include respondToLandscapeTablet {
      //   $margin: 16px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(25% - #{5 * $margin/4} - 0.5px);
      //   }
      // }

      const wishListMargin = 16
      return `calc(25% - ${(5 * wishListMargin) / 4}px - 0.5px)`
    } else if (this.windowWidth < 1600) {
      // $margin: 16px;
      // .wish-sizer,
      // .wish {
      //   $adjustment: 6 * $margin/5;
      //   width: calc(20% - #{$adjustment} - 0.5px);
      // }

      const wishListMargin = 16
      return `calc(20% - ${(6 * wishListMargin) / 5}px - 0.5px)`
    } else if (this.windowWidth < 1920) {
      // @media (min-width: 1600px) {
      //   $margin: 16px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(16.67% - #{7 * $margin/6} - 0.5px);
      //   }
      // }

      const wishListMargin = 16
      return `calc(16.67% - ${(7 * wishListMargin) / 6}px - 0.5px)`
    } else {
      // @include respondToHdScreen {
      //   $margin: 16px;
      //   .wish-sizer,
      //   .wish {
      //     width: calc(14.29% - #{8 * $margin/7} - 0.5px);
      //   }
      // }

      const wishListMargin = 16
      return `calc(14.29% - ${(8 * wishListMargin) / 7}px - 0.5px)`
    }
  }
}
